<template>
  <div class="product-detail-container">
    <!-- 动态粒子背景 -->
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>

    <!-- 顶部导航 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="back-btn" @click="goBack">
          <el-icon><ArrowLeft /></el-icon>
          <span>返回</span>
        </div>
        <div class="logo">
          <el-icon class="logo-icon"><ShoppingBag /></el-icon>
          <span class="glow-text">云商城</span>
        </div>
        <div class="nav-actions">
          <el-button @click="goToCart" type="primary" :icon="ShoppingCart">购物车</el-button>
          <el-button @click="goToHome" text>首页</el-button>
        </div>
      </div>
    </div>

    <!-- 商品详情内容 -->
    <div class="detail-content" v-loading="loading" element-loading-text="加载中...">
      <div class="detail-wrapper" v-if="!loading && product">
        <!-- 左侧：商品图片 -->
        <div class="product-image-section glassmorphism">
          <div class="main-image">
            <el-image 
              :src="productImage" 
              fit="contain"
              :preview-src-list="[productImage]"
              class="product-main-img"
            >
              <template #error>
                <div class="image-placeholder">
                  <el-icon size="64" color="#ccc"><Goods /></el-icon>
                  <span>暂无图片</span>
                </div>
              </template>
            </el-image>
          </div>
        </div>

        <!-- 右侧：商品信息 -->
        <div class="product-info-section glassmorphism">
          <div class="product-header">
            <h1 class="product-name">{{ product.title || product.name }}</h1>
            <div class="product-category" v-if="product.category">
              <el-tag size="large" type="info">{{ product.category }}</el-tag>
            </div>
          </div>

          <div class="price-section">
            <div class="current-price">
              <span class="price-label">价格</span>
              <span class="price-value">¥{{ product.price }}</span>
            </div>
          </div>

          <div class="stock-section">
            <div class="stock-item">
              <span class="label">库存</span>
              <span class="value" :class="{ 'low-stock': product.stock < 10 }">
                {{ product.stock }} 件
              </span>
            </div>
            <div class="stock-item" v-if="product.id">
              <span class="label">商品编号</span>
              <span class="value">{{ product.id }}</span>
            </div>
          </div>

          <div class="description-section" v-if="product.description">
            <h3 class="section-title">商品描述</h3>
            <p class="description-text">{{ product.description }}</p>
          </div>

          <div class="description-section" v-else>
            <h3 class="section-title">商品描述</h3>
            <p class="description-text placeholder">暂无商品描述</p>
          </div>

          <!-- 操作按钮 -->
          <div class="action-section">
            <el-input-number 
              v-model="buyCount" 
              :min="1" 
              :max="product.stock"
              class="count-input"
            />
            <el-button 
              type="primary" 
              size="large"
              :icon="ShoppingCart"
              @click="handleAddToCart"
              :disabled="product.stock === 0"
              class="add-cart-btn"
            >
              加入购物车
            </el-button>
            <el-button 
              type="danger" 
              size="large"
              plain
              @click="handleBuyNow"
              :disabled="product.stock === 0"
              class="buy-now-btn"
            >
              立即购买
            </el-button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div class="empty-state" v-if="!loading && !product">
        <el-icon size="64" color="#ccc"><Goods /></el-icon>
        <p>商品不存在或已下架</p>
        <el-button type="primary" @click="goToHome">返回首页</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ShoppingBag, ShoppingCart, Goods, ArrowLeft } from '@element-plus/icons-vue'
import { productService } from '../api/product'
import { cartService, getCurrentUserId } from '../api/order'

const router = useRouter()
const route = useRoute()

const loading = ref(true)
const product = ref(null)
const buyCount = ref(1)

const particleCanvas = ref(null)
let particleAnimationId = null

// 默认占位图片
const defaultImage = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="%23f0f2f5" width="300" height="300"/><text x="150" y="145" text-anchor="middle" fill="%23bbb" font-size="16" font-family="sans-serif">暂无图片</text><path d="M130 170h40l-8-12-7 9-5-4z" fill="%23ccc"/><circle cx="140" cy="155" r="4" fill="%23ccc"/></svg>')}`

// 获取商品图片
const productImage = computed(() => {
  if (!product.value) return ''
  return getProductImage(product.value.picUrl, product.value)
})

// 图片匹配逻辑（与Home.vue保持一致）
const productImageMap = {
  // 手机
  'iphone': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  'mate': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'galaxy': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'pixel': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'xiaomi': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'oppo': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'vivo': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'redmi': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  '手机': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  // 电脑/笔记本
  'macbook': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  'thinkpad': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '笔记本': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '电脑': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  'ipad': 'https://cdn-icons-png.flaticon.com/512/2329/2329087.png',
  '平板': 'https://cdn-icons-png.flaticon.com/512/2329/2329087.png',
  // 耳机/音频
  'airpods': 'https://cdn-icons-png.flaticon.com/512/2652/2652218.png',
  '耳机': 'https://cdn-icons-png.flaticon.com/512/2652/2652218.png',
  '音箱': 'https://cdn-icons-png.flaticon.com/512/3659/3659784.png',
  '音响': 'https://cdn-icons-png.flaticon.com/512/3659/3659784.png',
  // 手表/穿戴
  '手表': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  'watch': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  '手环': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  // 相机
  '相机': 'https://cdn-icons-png.flaticon.com/512/2956/2956744.png',
  '摄像': 'https://cdn-icons-png.flaticon.com/512/2956/2956744.png',
  // 家电
  '冰箱': 'https://cdn-icons-png.flaticon.com/512/3062/3062634.png',
  '洗衣机': 'https://cdn-icons-png.flaticon.com/512/3062/3062621.png',
  '空调': 'https://cdn-icons-png.flaticon.com/512/3062/3062626.png',
  '电视': 'https://cdn-icons-png.flaticon.com/512/2847/2847039.png',
  '微波炉': 'https://cdn-icons-png.flaticon.com/512/3062/3062649.png',
  // 服装
  '衣': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '裤': 'https://cdn-icons-png.flaticon.com/512/2503/2503387.png',
  '鞋': 'https://cdn-icons-png.flaticon.com/512/2742/2742528.png',
  '帽': 'https://cdn-icons-png.flaticon.com/512/2503/2503266.png',
  '服装': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  // 美妆
  '口红': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '面膜': 'https://cdn-icons-png.flaticon.com/512/2553/2553695.png',
  '护肤': 'https://cdn-icons-png.flaticon.com/512/2553/2553695.png',
  '香水': 'https://cdn-icons-png.flaticon.com/512/2553/2553642.png',
  '美妆': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  // 食品
  '零食': 'https://cdn-icons-png.flaticon.com/512/2515/2515183.png',
  '饮料': 'https://cdn-icons-png.flaticon.com/512/2405/2405479.png',
  '茶': 'https://cdn-icons-png.flaticon.com/512/2935/2935406.png',
  '咖啡': 'https://cdn-icons-png.flaticon.com/512/924/924514.png',
  '食品': 'https://cdn-icons-png.flaticon.com/512/1046/1046784.png',
  // 运动
  '跑步': 'https://cdn-icons-png.flaticon.com/512/2742/2742528.png',
  '篮球': 'https://cdn-icons-png.flaticon.com/512/484/484482.png',
  '健身': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '运动': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '户外': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  // 图书
  '书': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
  '图书': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
  // 游戏
  '游戏': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  'switch': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  'ps5': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  // 键盘/鼠标
  '键盘': 'https://cdn-icons-png.flaticon.com/512/2381/2381590.png',
  '鼠标': 'https://cdn-icons-png.flaticon.com/512/2381/2381573.png',
  // 分类兜底
  '手机数码': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  '电脑办公': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '家用电器': 'https://cdn-icons-png.flaticon.com/512/2847/2847039.png',
  '服装鞋帽': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '美妆个护': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '食品饮料': 'https://cdn-icons-png.flaticon.com/512/1046/1046784.png',
  '运动户外': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '图书音像': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
}

const getProductImage = (picUrl, product) => {
  // 先尝试根据商品名匹配本地图片库（更可靠）
  if (product) {
    const searchText = ((product.title || '') + (product.name || '') + (product.category || '')).toLowerCase()
    for (const [keyword, imgUrl] of Object.entries(productImageMap)) {
      if (searchText.includes(keyword.toLowerCase())) {
        return imgUrl
      }
    }
  }

  // 如果有有效的 picUrl 且不是随机图片服务，用作兜底
  if (picUrl && typeof picUrl === 'string' && picUrl.startsWith('http')
      && !picUrl.includes('picsum') && !picUrl.includes('placeholder') 
      && !picUrl.includes('unsplash') && !picUrl.includes('lorem') 
      && !picUrl.includes('random') && !picUrl.includes('loremflickr')
      && !picUrl.includes('example.com') && !picUrl.includes('via.placeholder')) {
    return picUrl
  }
  
  return defaultImage
}

// 获取商品详情
const fetchProductDetail = async () => {
  loading.value = true
  try {
    const productId = route.params.id
    if (!productId) {
      ElMessage.error('商品ID不存在')
      return
    }

    // 先尝试从后端获取商品详情
    try {
      const response = await productService.getProductList({ id: productId })
      const data = response.data
      
      // 处理不同的返回格式
      if (Array.isArray(data) && data.length > 0) {
        product.value = data[0]
      } else if (data.records && data.records.length > 0) {
        product.value = data.records[0]
      } else if (data.data) {
        product.value = Array.isArray(data.data) ? data.data[0] : data.data
      } else {
        // 如果后端没有返回数据，使用传递的商品信息
        const productData = route.query.product
        if (productData) {
          product.value = JSON.parse(decodeURIComponent(productData))
        }
      }
    } catch (error) {
      console.error('获取商品详情失败:', error)
      // 如果API调用失败，尝试使用路由传递的商品信息
      const productData = route.query.product
      if (productData) {
        product.value = JSON.parse(decodeURIComponent(productData))
      }
    }

    if (!product.value) {
      ElMessage.error('商品不存在')
    }
  } catch (error) {
    console.error('加载商品详情异常:', error)
    ElMessage.error('加载失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 加入购物车
const handleAddToCart = async () => {
  try {
    const userId = getCurrentUserId()
    await cartService.addCart({
      userId,
      productId: product.value.id,
      count: buyCount.value
    })
    ElMessage.success(`已加入购物车 ${buyCount.value} 件`)
  } catch (error) {
    console.error('加入购物车失败:', error)
    ElMessage.error(error.response?.data?.message || '加入购物车失败')
  }
}

// 立即购买
const handleBuyNow = async () => {
  try {
    const userId = getCurrentUserId()
    await cartService.addCart({
      userId,
      productId: product.value.id,
      count: buyCount.value
    })
    router.push('/cart')
  } catch (error) {
    console.error('操作失败:', error)
    ElMessage.error(error.response?.data?.message || '操作失败')
  }
}

// 粒子动画
const initParticles = () => {
  const canvas = particleCanvas.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight

  const particles = []
  const particleCount = 50

  for (let i = 0; i < particleCount; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: Math.random() * 2 + 1,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      opacity: Math.random() * 0.5 + 0.2
    })
  }

  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    particles.forEach(particle => {
      particle.x += particle.vx
      particle.y += particle.vy

      if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1
      if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1

      ctx.beginPath()
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(163, 163, 255, ${particle.opacity})`
      ctx.fill()
    })

    particleAnimationId = requestAnimationFrame(animate)
  }

  animate()
}

// 导航方法
const goBack = () => {
  router.back()
}

const goToHome = () => {
  router.push('/home')
}

const goToCart = () => {
  router.push('/cart')
}

onMounted(() => {
  fetchProductDetail()
  initParticles()
  
  window.addEventListener('resize', () => {
    if (particleCanvas.value) {
      particleCanvas.value.width = window.innerWidth
      particleCanvas.value.height = window.innerHeight
    }
  })
})

onUnmounted(() => {
  if (particleAnimationId) {
    cancelAnimationFrame(particleAnimationId)
  }
})
</script>

<style scoped>
.product-detail-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #232a4d 0%, #1a1f3a 50%, #2d1b3d 100%);
  position: relative;
  overflow-x: hidden;
  padding-bottom: 40px;
}

.particle-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

/* 顶部导航 */
.nav-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(35, 42, 77, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(163, 163, 255, 0.2);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 15px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #a3a3ff;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s;
  padding: 8px 16px;
  border-radius: 8px;
}

.back-btn:hover {
  background: rgba(163, 163, 255, 0.1);
  color: #d4d4ff;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: bold;
  color: #fff;
}

.logo-icon {
  font-size: 28px;
  color: #a3a3ff;
}

.glow-text {
  text-shadow: 0 0 10px rgba(163, 163, 255, 0.5);
}

.nav-actions {
  display: flex;
  gap: 12px;
}

/* 详情内容 */
.detail-content {
  max-width: 1400px;
  margin: 30px auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;
}

.detail-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.glassmorphism {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* 商品图片区域 */
.product-image-section {
  padding: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
}

.main-image {
  width: 100%;
  max-width: 500px;
}

.product-main-img {
  width: 100%;
  height: auto;
  border-radius: 12px;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #999;
}

/* 商品信息区域 */
.product-info-section {
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.product-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-name {
  font-size: 32px;
  font-weight: bold;
  color: #fff;
  margin: 0;
  line-height: 1.4;
}

.product-category {
  display: flex;
  gap: 8px;
}

/* 价格区域 */
.price-section {
  padding: 24px;
  background: linear-gradient(135deg, rgba(163, 163, 255, 0.1), rgba(163, 163, 255, 0.05));
  border-radius: 12px;
  border: 1px solid rgba(163, 163, 255, 0.2);
}

.current-price {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.price-label {
  font-size: 16px;
  color: #a3a3ff;
}

.price-value {
  font-size: 42px;
  font-weight: bold;
  color: #ff6b6b;
}

/* 库存区域 */
.stock-section {
  display: flex;
  gap: 40px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
}

.stock-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stock-item .label {
  font-size: 14px;
  color: #999;
}

.stock-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #4CAF50;
}

.stock-item .value.low-stock {
  color: #ff6b6b;
}

/* 描述区域 */
.description-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 0;
}

.description-text {
  font-size: 16px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

.description-text.placeholder {
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

/* 操作区域 */
.action-section {
  display: flex;
  gap: 16px;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.count-input {
  width: 120px;
}

.add-cart-btn {
  flex: 1;
  height: 48px;
  font-size: 16px;
}

.buy-now-btn {
  flex: 1;
  height: 48px;
  font-size: 16px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 100px 20px;
  color: rgba(255, 255, 255, 0.6);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .detail-wrapper {
    grid-template-columns: 1fr;
  }

  .product-image-section {
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .nav-content {
    padding: 12px 20px;
  }

  .detail-content {
    padding: 0 20px;
  }

  .product-image-section,
  .product-info-section {
    padding: 24px;
  }

  .product-name {
    font-size: 24px;
  }

  .price-value {
    font-size: 32px;
  }

  .action-section {
    flex-direction: column;
  }

  .count-input,
  .add-cart-btn,
  .buy-now-btn {
    width: 100%;
  }
}
</style>
